/*
  SectionCategoriesCard — Settings card for the Exercise Program
  section-category dropdown catalog.

  Sits under `ExerciseProgramTemplatesCard` in Settings → Clinical.
  Owners + managers get add / rename / drag-to-reorder / delete;
  other roles see a read-only list.

  Reorder is powered by @dnd-kit. Dropping a row triggers a single
  PUT per moved neighbour so we don't renumber the whole list on
  every drag — the two rows around the drop swap sort_order.

  Each row shows a "N in use" chip so operators know how many
  active programs still reference the category before they delete
  it. Delete is soft (see backend) so historical labels persist.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Layers, Plus, Trash2, Check, X, Pencil, Loader2, GripVertical,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import UsageDrilldownDialog from "@/components/UsageDrilldownDialog";

export default function SectionCategoriesCard() {
  const { can } = useAuth();
  const canEdit = can("owner", "manager");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [editingKey, setEditingKey] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const [drilldown, setDrilldown] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/exercise-programs/section-categories");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Couldn't load section categories", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleAdd() {
    const label = newLabel.trim();
    if (!label) { toast.error("Give the category a name first."); return; }
    setBusyKey("__new__");
    try {
      const maxOrder = items.reduce(
        (m, i) => Math.max(m, i.sort_order || 0), 0);
      await api.post("/exercise-programs/section-categories",
        { label, sort_order: maxOrder + 10 });
      setNewLabel("");
      setAdding(false);
      toast.success("Category added");
      load();
    } catch (e) {
      toast.error("Add failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusyKey(null); }
  }

  async function handleRename(key) {
    const label = editLabel.trim();
    if (!label) { toast.error("Label can't be empty."); return; }
    setBusyKey(key);
    try {
      await api.put(`/exercise-programs/section-categories/${encodeURIComponent(key)}`,
        { label });
      setEditingKey(null);
      setEditLabel("");
      toast.success("Renamed");
      load();
    } catch (e) {
      toast.error("Rename failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusyKey(null); }
  }

  async function handleDelete(key, label, usageCount) {
    const inUseWarn = usageCount > 0
      ? `\n\n⚠️  ${usageCount} active program${usageCount === 1 ? "" : "s"} `
        + `still reference${usageCount === 1 ? "s" : ""} "${label}". `
        + `Those sections keep their label — only the dropdown hides it.`
      : "";
    if (!window.confirm(`Delete "${label}" from the dropdown?${inUseWarn}`)) return;
    setBusyKey(key);
    try {
      await api.delete(`/exercise-programs/section-categories/${encodeURIComponent(key)}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusyKey(null); }
  }

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.key === active.id);
    const to = items.findIndex((i) => i.key === over.id);
    if (from < 0 || to < 0) return;
    // Optimistically reorder + renumber to (index+1)*10 so the list
    // stays clean-looking and future inserts have room between rows.
    const next = arrayMove(items, from, to).map((it, idx) => ({
      ...it, sort_order: (idx + 1) * 10,
    }));
    setItems(next);
    // Persist only the rows whose sort_order actually changed.
    const changed = next.filter(
      (it, idx) => it.sort_order !== items.find((o) => o.key === it.key)?.sort_order,
    );
    try {
      await Promise.all(changed.map((it) =>
        api.put(
          `/exercise-programs/section-categories/${encodeURIComponent(it.key)}`,
          { sort_order: it.sort_order },
        ),
      ));
    } catch (e) {
      toast.error("Reorder failed", {
        description: e?.response?.data?.detail || e.message,
      });
      load();  // roll back to server-truth
    }
  }

  return (
    <Card id="section-categories" className="p-4 rounded-lg border border-zinc-200 shadow-none"
          data-testid="section-categories-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-800">
            <Layers className="h-4 w-4" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-600 font-semibold">
              Program Section Categories
            </div>
            <div className="text-sm text-zinc-700 leading-relaxed">
              The dropdown of section types (Warm-up, Mobility, Pilates · Reformer, …) shown when
              editing any exercise program. Drag the handle to reorder — deleting a category only
              hides it from the dropdown, existing programs keep their labels.
            </div>
          </div>
        </div>
        {canEdit && !adding && (
          <Button size="sm"
                  onClick={() => setAdding(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-1"
                  data-testid="section-categories-add-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add category
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-600 flex items-center gap-2 py-4">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {adding && (
            <div className="flex items-center gap-2 border border-indigo-200 bg-indigo-50/50 rounded-md p-2 mb-2"
                 data-testid="section-categories-add-row">
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="New category name (e.g. Aqua)"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); handleAdd(); }
                  if (e.key === "Escape") { setAdding(false); setNewLabel(""); }
                }}
                className="h-8 text-sm"
                data-testid="section-categories-new-label"
              />
              <Button size="sm"
                      onClick={handleAdd}
                      disabled={busyKey === "__new__"}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white"
                      data-testid="section-categories-save-new">
                {busyKey === "__new__"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Check className="h-3.5 w-3.5" />}
              </Button>
              <Button size="sm" variant="ghost"
                      onClick={() => { setAdding(false); setNewLabel(""); }}
                      data-testid="section-categories-cancel-new">
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((i) => i.key)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-md overflow-hidden"
                  data-testid="section-categories-list">
                {items.map((it) => (
                  <CategoryRow
                    key={it.key}
                    item={it}
                    canEdit={canEdit}
                    isEditing={editingKey === it.key}
                    editLabel={editLabel}
                    setEditLabel={setEditLabel}
                    onStartEdit={() => { setEditingKey(it.key); setEditLabel(it.label); }}
                    onCancelEdit={() => { setEditingKey(null); setEditLabel(""); }}
                    onSaveEdit={() => handleRename(it.key)}
                    onDelete={() => handleDelete(it.key, it.label, it.usage_count || 0)}
                    onUsageClick={() => setDrilldown({
                      url: `/exercise-programs/section-categories/${encodeURIComponent(it.key)}/usage`,
                      title: `Programs using "${it.label}"`,
                    })}
                    busy={busyKey === it.key}
                  />
                ))}
                {items.length === 0 && (
                  <li className="px-3 py-4 text-sm text-zinc-600 text-center"
                      data-testid="section-categories-empty">
                    No categories yet. Add one to start populating the dropdown.
                  </li>
                )}
              </ul>
            </SortableContext>
          </DndContext>
          <div className="mt-2 text-2xs text-zinc-500">
            Tip: grab the ⋮⋮ handle on any row to drag it into a new position.
          </div>
        </>
      )}
      {drilldown && (
        <UsageDrilldownDialog
          open={!!drilldown}
          onOpenChange={(v) => { if (!v) setDrilldown(null); }}
          fetchUrl={drilldown.url}
          title={drilldown.title}
        />
      )}
    </Card>
  );
}


/**
 * CategoryRow — single sortable list row. Kept as a sibling
 * component so useSortable() only fires per-row (attaching to the
 * ul would break the hook contract).
 */
function CategoryRow({
  item, canEdit, isEditing, editLabel, setEditLabel,
  onStartEdit, onCancelEdit, onSaveEdit, onDelete, onUsageClick, busy,
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: item.key, disabled: !canEdit || isEditing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : "auto",
  };

  const usage = item.usage_count || 0;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-2 px-2 py-1.5 bg-white",
        isDragging ? "shadow-md ring-1 ring-indigo-200 bg-indigo-50/50" : "hover:bg-zinc-50",
      )}
      data-testid={`section-categories-row-${item.key}`}
    >
      {canEdit && !isEditing ? (
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-0.5 text-zinc-400 hover:text-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 rounded"
          title="Drag to reorder"
          aria-label={`Drag ${item.label}`}
          data-testid={`section-categories-drag-${item.key}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      ) : (
        <span className="w-5" />
      )}
      {isEditing ? (
        <>
          <Input
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); onSaveEdit(); }
              if (e.key === "Escape") { onCancelEdit(); }
            }}
            className="h-7 text-sm flex-1"
            data-testid={`section-categories-edit-input-${item.key}`}
          />
          <Button size="sm"
                  onClick={onSaveEdit}
                  disabled={busy}
                  className="h-7 bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid={`section-categories-save-${item.key}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          </Button>
          <Button size="sm" variant="ghost"
                  onClick={onCancelEdit}
                  className="h-7"
                  data-testid={`section-categories-cancel-${item.key}`}>
            <X className="h-3 w-3" />
          </Button>
        </>
      ) : (
        <>
          <span className="text-sm flex-1 text-zinc-900 truncate">{item.label}</span>
          <UsageChip count={usage}
                      testId={`section-categories-usage-${item.key}`}
                      onClick={usage ? onUsageClick : undefined} />
          <span className="text-2xs uppercase tracking-wider text-zinc-500 font-mono-num">
            {item.key}
          </span>
          {canEdit && (
            <div className="flex items-center gap-0.5">
              <button
                onClick={onStartEdit}
                disabled={busy}
                className="h-7 w-7 grid place-items-center rounded hover:bg-zinc-100 text-zinc-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
                title="Rename"
                aria-label={`Rename ${item.label}`}
                data-testid={`section-categories-rename-${item.key}`}>
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={onDelete}
                disabled={busy}
                className="h-7 w-7 grid place-items-center rounded hover:bg-red-50 text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600"
                title={usage > 0
                  ? `${usage} program${usage === 1 ? "" : "s"} still use this — click to delete anyway`
                  : "Delete"}
                aria-label={`Delete ${item.label}`}
                data-testid={`section-categories-delete-${item.key}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </>
      )}
    </li>
  );
}


/** Small pill showing "N in use" — hidden when count is 0 to keep
 *  the row tidy. Amber tone signals "look before you leap" without
 *  screaming error red at every category.
 *
 *  When ``onClick`` is supplied the chip becomes a real button and
 *  opens the caller's drill-down dialog on click / Enter / Space.
 */
export function UsageChip({ count, testId, onClick }) {
  if (!count) return null;
  const baseClass = "inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 text-amber-900 text-2xs font-medium px-1.5 py-0.5";
  const label = `${count} active program${count === 1 ? "" : "s"} reference this — click to see which`;
  const inner = (<><span className="tabular-nums">{count}</span> in use</>);
  if (onClick) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className={`${baseClass} hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 cursor-pointer`}
        title={label}
        aria-label={label}
        data-testid={testId}
      >
        {inner}
      </button>
    );
  }
  return (
    <span className={baseClass}
          title={`${count} active program${count === 1 ? "" : "s"} reference this`}
          data-testid={testId}>
      {inner}
    </span>
  );
}
